import { useCallback, useRef, useState } from 'react';

import { AdminApiError, adminApiFetch } from './api-client';

import type { CompletedPart, InitMediaUploadResponse } from '@utazzo/shared';

export type MediaUploadStatus =
  | 'idle'
  | 'initiating'
  | 'uploading'
  | 'finalizing'
  | 'ready'
  | 'error';

type MediaKind = 'IMAGE' | 'VIDEO' | 'DOCUMENT';

export interface MediaUploadHandle<TAsset = unknown> {
  upload: (file: File, opts?: { kind?: MediaKind; folder?: string }) => Promise<TAsset>;
  /** 0-1 fraction. */
  progress: number;
  status: MediaUploadStatus;
  error?: AdminApiError;
  reset: () => void;
}

const PART_CONCURRENCY = 3;
const PART_RETRIES = 3;

/**
 * Direct-to-S3 upload with automatic single-PUT vs multipart selection
 * (the API decides by file size and returns the right shape):
 *   1. POST /upload-init                 → presigned URL(s)
 *   2. PUT  <url> | PUT each part        → S3 (no auth header)
 *   3. POST /upload-finalize             → server completes + verifies
 * On multipart failure we POST /upload-abort to release the staged parts.
 *
 * Steps 2 use XMLHttpRequest for upload progress events. Steps 1/3 go through
 * `adminApiFetch` for shared auth + error handling.
 */
export function useMediaUpload<TAsset = unknown>(): MediaUploadHandle<TAsset> {
  const [status, setStatus] = useState<MediaUploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<AdminApiError | undefined>(undefined);
  const aborted = useRef(false);

  const reset = useCallback((): void => {
    aborted.current = true;
    setStatus('idle');
    setProgress(0);
    setError(undefined);
  }, []);

  const upload = useCallback(
    async (file: File, opts: { kind?: MediaKind; folder?: string } = {}): Promise<TAsset> => {
      aborted.current = false;
      setError(undefined);
      setProgress(0);
      setStatus('initiating');

      const kind = opts.kind ?? inferKind(file.type);

      let init: InitMediaUploadResponse;
      try {
        init = await adminApiFetch<InitMediaUploadResponse>('/admin/website/media/upload-init', {
          method: 'POST',
          body: {
            kind,
            mimeType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
            fileName: file.name,
            folder: opts.folder ?? null,
          },
        });
      } catch (e) {
        return failAndThrow(e, setError, setStatus);
      }

      setStatus('uploading');
      let parts: CompletedPart[] | undefined;
      try {
        const report = (loaded: number): void => {
          if (!aborted.current) setProgress(Math.min(1, loaded / file.size));
        };
        if (init.mode === 'single') {
          await putBytes(init.presignedUrl, file, report, file.type || undefined);
        } else {
          parts = await uploadParts(init, file, report, aborted);
        }
      } catch (e) {
        if (init.mode === 'multipart') {
          // Best-effort cleanup of the staged multipart session.
          await adminApiFetch('/admin/website/media/upload-abort', {
            method: 'POST',
            body: { assetId: init.assetId, uploadId: init.uploadId },
          }).catch(() => undefined);
        }
        return failAndThrow(e, setError, setStatus);
      }

      setStatus('finalizing');
      let asset: TAsset;
      try {
        asset = await adminApiFetch<TAsset>('/admin/website/media/upload-finalize', {
          method: 'POST',
          body:
            init.mode === 'multipart'
              ? { assetId: init.assetId, uploadId: init.uploadId, parts }
              : { assetId: init.assetId },
        });
      } catch (e) {
        return failAndThrow(e, setError, setStatus);
      }

      setProgress(1);
      setStatus('ready');
      return asset;
    },
    [],
  );

  return { upload, progress, status, error, reset };
}

/** Upload every part with bounded concurrency + per-part retry; returns ETags. */
async function uploadParts(
  init: Extract<InitMediaUploadResponse, { mode: 'multipart' }>,
  file: File,
  report: (loaded: number) => void,
  aborted: { current: boolean },
): Promise<CompletedPart[]> {
  const loaded = new Array<number>(init.parts.length).fill(0);
  const reportAll = (): void => report(loaded.reduce((a, b) => a + b, 0));
  const completed: CompletedPart[] = [];

  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < init.parts.length) {
      if (aborted.current)
        throw new AdminApiError({ status: 0, code: 'aborted', message: 'Upload cancelled' });
      const index = cursor++;
      const part = init.parts[index]!;
      const start = (part.partNumber - 1) * init.partSizeBytes;
      const blob = file.slice(start, Math.min(start + init.partSizeBytes, file.size));

      let etag: string | undefined;
      for (let attempt = 1; ; attempt += 1) {
        try {
          etag = await putBytes(part.url, blob, (l) => {
            loaded[index] = l;
            reportAll();
          });
          break;
        } catch (e) {
          if (attempt >= PART_RETRIES) throw e;
          loaded[index] = 0;
        }
      }
      if (!etag) {
        throw new AdminApiError({
          status: 0,
          code: 'missing_etag',
          message: 'S3 did not return an ETag for a part (check bucket CORS ExposeHeaders: ETag).',
        });
      }
      loaded[index] = blob.size;
      reportAll();
      completed.push({ partNumber: part.partNumber, etag });
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(PART_CONCURRENCY, init.parts.length) }, () => worker()),
  );
  return completed.sort((a, b) => a.partNumber - b.partNumber);
}

function inferKind(mime: string): MediaKind {
  if (mime.startsWith('image/')) return 'IMAGE';
  if (mime.startsWith('video/')) return 'VIDEO';
  return 'DOCUMENT';
}

function failAndThrow(
  e: unknown,
  setError: (err: AdminApiError) => void,
  setStatus: (s: MediaUploadStatus) => void,
): never {
  const err =
    e instanceof AdminApiError
      ? e
      : new AdminApiError({
          status: 0,
          code: 'network_error',
          message: e instanceof Error ? e.message : 'Upload failed',
        });
  setError(err);
  setStatus('error');
  throw err;
}

// Cap each PUT so a stalled request fails loudly. Scaled to body size.
const putTimeoutMs = (bytes: number): number =>
  Math.min(10 * 60_000, Math.max(60_000, Math.round((bytes / (250 * 1024)) * 1000)));

/**
 * PUT a blob to a presigned URL. Resolves with the response ETag (needed to
 * complete multipart uploads). `contentType` is set only for single-PUT URLs
 * that were signed with it; multipart part URLs are not, so it's omitted.
 */
function putBytes(
  url: string,
  body: Blob,
  onLoaded: (loaded: number) => void,
  contentType?: string,
): Promise<string | undefined> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.timeout = putTimeoutMs(body.size);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (e.lengthComputable) onLoaded(e.loaded);
    };
    const fail = (status: number, code: string, message: string): void =>
      reject(new AdminApiError({ status, code, message }));
    xhr.onload = (): void => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag') ?? undefined);
      } else {
        fail(
          xhr.status,
          's3_upload_failed',
          xhr.responseText || `S3 upload failed (${xhr.status})`,
        );
      }
    };
    xhr.onerror = (): void =>
      fail(
        0,
        'network_error',
        'S3 upload failed - check the bucket CORS policy allows PUT from this origin.',
      );
    xhr.ontimeout = (): void => fail(0, 'timeout', 'S3 upload timed out.');
    xhr.send(body);
  });
}
